import moment from 'moment';

import type { VerificationRunCheck } from 'api/bitedb/entities/businessVerification.service';
import {
  businessVerificationReportPDF,
  IBusinessVerificationReportData,
} from 'components/Common/EntityTicketPdf';

import {
  buildEntityVerificationReportChecks,
  formatVerificationDate,
  latestVerificationRun,
  recordFieldValue,
  VERIFICATION_FIELD_LABELS,
  VERIFICATION_RECORD_FIELDS,
  verificationNextTitle,
  VerificationReportCheck,
  verificationShowsEvidence,
  verificationStatusLabel,
} from './entityVerificationReportModel';
import {
  NEEDS_INPUT_REASONS,
  NOT_COVERED_LABEL,
  pageRowsFedBy,
  runCheckErrorFor,
} from './verificationCheckErrors';
import {
  describeVerificationResult,
  entityStatusLabel,
  resolveVerificationPageState,
} from './verificationReportSummary';

const SKIP_REASONS: Record<string, string> = {
  no_domain: 'no domain on file',
  no_email: 'no contact email on file',
  no_phone: 'no contact phone on file',
  no_address: 'no address on file',
  no_name_or_registration: 'no name or registration number on file',
  registry_not_covered: 'the registry covers Canadian companies only',
  ...NEEDS_INPUT_REASONS,
};

const formatCheckedAt = (value: string | null): string =>
  value ? moment(value).format('MMM D, YYYY · h:mm A') : '';

const runNotesFor = (
  runChecks: VerificationRunCheck[] | undefined,
  checks: VerificationReportCheck[],
): string[] => {
  if (!runChecks || !runChecks.length) return [];
  const ran = pageRowsFedBy(
    runChecks.filter((check) => check.status !== 'skipped'),
    checks,
  );
  const notCovered = ran.filter((row) => row.tone === 'unavailable');
  const errored = ran.filter((row) => runCheckErrorFor(row, runChecks));
  const updated = ran.filter(
    (row) => !errored.includes(row) && !notCovered.includes(row),
  );

  const notes: string[] = [];
  if (updated.length) {
    notes.push(`Updated: ${updated.map((row) => row.label).join(', ')}.`);
  }
  errored.forEach((row) =>
    notes.push(
      verificationStatusLabel({
        ...row,
        error: runCheckErrorFor(row, runChecks),
      }),
    ),
  );
  notCovered.forEach((row) =>
    notes.push(`Not run: ${row.label} — ${NOT_COVERED_LABEL.toLowerCase()}.`),
  );
  const skipped = new Map<string, string>();
  runChecks
    .filter((check) => check.status === 'skipped')
    .forEach((check) => {
      pageRowsFedBy([check], checks)
        .filter((row) => !ran.includes(row))
        .forEach((row) => {
          if (!skipped.has(row.label)) {
            skipped.set(
              row.label,
              SKIP_REASONS[check.reason || ''] || 'not run',
            );
          }
        });
    });
  skipped.forEach((reason, label) =>
    notes.push(`Not run: ${label} — ${reason}.`),
  );
  return notes;
};

export const buildVerificationReportData = ({
  supplier,
  databaseName,
  preparedBy,
  runChecks,
}: {
  supplier: any;
  databaseName?: string;
  preparedBy?: string;
  runChecks?: VerificationRunCheck[];
}): IBusinessVerificationReportData => {
  const checks = buildEntityVerificationReportChecks(supplier || {});
  const lastRunAt = latestVerificationRun(checks);
  const state = resolveVerificationPageState({
    supplier: supplier,
    checks: checks,
    hasPlan: true,
    running: false,
  });
  const summary = describeVerificationResult({
    state: state,
    supplier: supplier,
    checks: checks,
    databaseName: databaseName || 'this database',
  });

  return {
    entityName: supplier?.name || 'Entity',
    databaseName: databaseName,
    country: supplier?.country_of_origin || undefined,
    status: entityStatusLabel(supplier?.status_flag),
    lastVerified: formatCheckedAt(lastRunAt),
    preparedBy: preparedBy,
    figure: summary.figure,
    figureTone: summary.figureTone,
    tail: summary.tail,
    summary: summary.summary,
    counts: {
      passed: checks.filter((check) => check.tone === 'passed').length,
      attention: checks.filter(
        (check) => check.tone === 'review' || check.tone === 'input',
      ).length,
      failed: checks.filter((check) => check.tone === 'failed').length,
    },
    runNotes: runNotesFor(runChecks, checks),
    checks: checks.map((check) => {
      return {
        label: check.label,
        tone: check.tone,
        statusLabel: verificationStatusLabel(check),
        description: check.description,
        nextTitle: verificationNextTitle(check),
        next: check.next,
        reads: check.reads.map((field) => ({
          label: VERIFICATION_FIELD_LABELS[field],
          value: recordFieldValue(supplier, field) || 'Not set',
        })),
        evidence: verificationShowsEvidence(check) ? check.details : [],
        checkedAt: formatCheckedAt(check.checkedAt),
        checkedOn: formatVerificationDate(check.checkedAt),
      };
    }),
    fields: VERIFICATION_RECORD_FIELDS.map((field) => {
      const value = recordFieldValue(supplier, field);
      const readBy = checks.filter((check) => check.reads.includes(field));
      const neededBy = readBy.filter(
        (check) => check.tone === 'input' && check.fix === field,
      );
      return {
        label: VERIFICATION_FIELD_LABELS[field],
        value: value || 'Not set',
        usedBy:
          !value && neededBy.length
            ? `Needed by ${neededBy.map((check) => check.label).join(', ')}`
            : readBy.map((check) => check.label).join(', '),
      };
    }),
  };
};

/** The signed-in user's name, for the report's "Prepared by" row. */
export const reportPreparedBy = (
  user?: { basics?: { firstName?: string; lastName?: string } } | null,
): string =>
  [user?.basics?.firstName, user?.basics?.lastName].filter(Boolean).join(' ');

/** "Business Verification - Acme Ltd - 2026-10-02.pdf", safe on every OS. */
export const verificationReportFileName = (entityName: string): string =>
  `Business Verification - ${
    String(entityName || 'Entity')
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'Entity'
  } - ${moment().format('YYYY-MM-DD')}.pdf`;

export const downloadVerificationReport = (
  options: Parameters<typeof buildVerificationReportData>[0],
): Promise<Blob> => {
  const data = buildVerificationReportData(options);
  return businessVerificationReportPDF(
    data,
    verificationReportFileName(data.entityName),
  );
};
